import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaUpload, FaSpinner, FaTrash, FaPlus } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  getDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import imageCompression from 'browser-image-compression';

const Container = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: #f5f5f5;
  color: #333;
  margin-right: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e5e5e5;
  }
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #555;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const RichTextEditorWrapper = styled.div`
  .quill {
    background-color: white;
    border-radius: 4px;
  }
  
  .ql-toolbar {
    border: 1px solid #ddd;
    border-radius: 4px 4px 0 0;
    background-color: #f8f8f8;
  }
  
  .ql-container {
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
    min-height: 200px;
    font-size: 1rem;
    font-family: inherit;
  }
  
  .ql-editor {
    min-height: 200px;
  }
  
  .ql-editor:focus {
    outline: none;
  }
  
  .quill:focus-within .ql-toolbar,
  .quill:focus-within .ql-container {
    border-color: #C41E3A;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  
  input {
    margin-right: 0.5rem;
  }
`;

const ImageUploadContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const ImagePreviewsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SingleImagePreview = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteImageButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(196, 30, 58, 0.8);
  }
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e5e5e5;
  }
  
  input {
    display: none;
  }
`;

const ProgressBar = styled.div`
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const Progress = styled.div`
  height: 100%;
  background-color: #C41E3A;
  width: ${props => props.value}%;
  transition: width 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  grid-column: 1 / -1;
  margin-top: 1rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.$primary ? '#C41E3A' : 'white'};
  color: ${props => props.$primary ? 'white' : '#C41E3A'};
  border: ${props => props.$primary ? 'none' : '1px solid #C41E3A'};
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.$primary ? '#A01930' : '#f8f8f8'};
  }
  
  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #C41E3A;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(196, 30, 58, 0.1);
  border-radius: 4px;
  grid-column: 1 / -1;
`;

const CompressionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #666;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    description: '',
    image: '', // Keep for backward compatibility
    images: [], // New field for multiple images
    featured: false,
    inStock: true,
    stock: 0,
    weight: '',
    dimensions: '',
    material: '',
    condition: '',
    origin: '',
    year: ''
  });
  
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [compressionStatus, setCompressionStatus] = useState('');
  
  // Categories for dropdown - matches navigation bar categories
  const categories = [
    'Silver',
    'Copper',
    'Ancient/Shipwreck',
    'Gold',
    'Coins',
    'Rounds',
    'Gems & Stones',
    'Collectibles',
    'Merchandise',
    'Other'
  ];
  
  // Fetch product data if in edit mode
  useEffect(() => {
    const fetchProduct = async () => {
      if (isEditMode) {
        try {
          const productDoc = await getDoc(doc(db, 'products', id));
          if (productDoc.exists()) {
            const productData = productDoc.data();
            
            // Handle both old (single image) and new (multiple images) format
            if (productData.images && productData.images.length > 0) {
              setImagePreviews(productData.images);
            } else if (productData.image) {
              setImagePreviews([productData.image]);
            }
            
            setFormData({
              ...productData,
              // If images array doesn't exist, create it from the single image
              images: productData.images || (productData.image ? [productData.image] : [])
            });
          } else {
            setError('Product not found');
          }
        } catch (error) {
          console.error('Error fetching product:', error);
          setError('Error loading product data');
        }
      }
    };
    
    fetchProduct();
  }, [id, isEditMode]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check file types
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Some files were not added. Please ensure all images are valid (JPEG, PNG, or WebP).');
      return;
    }
    
    // Compress and process each image
    setCompressionStatus('Compressing images...');
    
    try {
      const compressionOptions = {
        maxSizeMB: 1,             // Max file size in MB
        maxWidthOrHeight: 1920,   // Max width/height in pixels
        useWebWorker: true,       // Use web worker for better performance
        initialQuality: 0.8,      // Initial quality (0-1)
      };
      
      const compressPromises = files.map(async (file) => {
        try {
          // Skip compression if file is already small (less than 200KB)
          if (file.size <= 200 * 1024) {
            return file;
          }
          
          const compressedFile = await imageCompression(file, compressionOptions);
          
          // Create a new File object with the same name but compressed data
          return new File([compressedFile], file.name, {
            type: compressedFile.type,
            lastModified: new Date().getTime()
          });
        } catch (error) {
          console.error('Error compressing image:', error);
          // If compression fails, return original file
          return file;
        }
      });
      
      const compressedFiles = await Promise.all(compressPromises);
      
      // Add new files to existing files
      setImageFiles(prevFiles => [...prevFiles, ...compressedFiles]);
      
      // Create and add new previews
      const newPreviews = compressedFiles.map(file => URL.createObjectURL(file));
      setImagePreviews(prevPreviews => [...prevPreviews, ...newPreviews]);
      
      setCompressionStatus('');
      setError('');
      
      // Log compression results
      const originalSize = files.reduce((total, file) => total + file.size, 0);
      const compressedSize = compressedFiles.reduce((total, file) => total + file.size, 0);
      const savedPercentage = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
      
      console.log(`Image compression complete. Reduced size by ${savedPercentage}% (${(originalSize/1024/1024).toFixed(2)}MB → ${(compressedSize/1024/1024).toFixed(2)}MB)`);
      
    } catch (error) {
      console.error('Error in image processing:', error);
      setError('Error processing images. Please try again.');
      setCompressionStatus('');
    }
  };
  
  const removeImage = (index) => {
    // Remove from previews
    setImagePreviews(prevPreviews => {
      const newPreviews = [...prevPreviews];
      newPreviews.splice(index, 1);
      return newPreviews;
    });
    
    // If it's a new image (has a corresponding file), remove from files
    if (index < imageFiles.length) {
      setImageFiles(prevFiles => {
        const newFiles = [...prevFiles];
        newFiles.splice(index, 1);
        return newFiles;
      });
    } else {
      // If it's an existing image, remove from formData.images
      setFormData(prevData => {
        const newImages = [...prevData.images];
        newImages.splice(index - imageFiles.length, 1);
        return {
          ...prevData,
          images: newImages
        };
      });
    }
  };
  
  const uploadImages = async () => {
    if (imageFiles.length === 0) return formData.images;
    
    setIsUploading(true);
    const uploadPromises = imageFiles.map(async (file, index) => {
      const storageRef = ref(storage, `products/${Date.now()}_${index}_${file.name}`);
      
      // Add metadata including content type and CORS settings
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type, Authorization'
        }
      };
      
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Calculate overall progress across all files
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            // This is simplified and will show progress of the current file
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              console.error('Error getting download URL:', error);
              reject(error);
            }
          }
        );
      });
    });
    
    try {
      const uploadedImageUrls = await Promise.all(uploadPromises);
      setIsUploading(false);
      
      // Combine existing images with new uploaded ones
      return [...formData.images, ...uploadedImageUrls];
    } catch (error) {
      setError('Error uploading one or more images');
      setIsUploading(false);
      throw error;
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Validate form
      if (!formData.name || !formData.category || !formData.price) {
        throw new Error('Please fill in all required fields');
      }
      
      // Validate price
      const price = parseFloat(formData.price);
      if (isNaN(price) || price <= 0) {
        throw new Error('Please enter a valid price');
      }
      
      // Validate images
      if (imagePreviews.length === 0) {
        throw new Error('Please upload at least one product image');
      }
      
      // Upload images if any new ones were selected
      let imageUrls = formData.images;
      if (imageFiles.length > 0) {
        imageUrls = await uploadImages();
      }
      
      // Prepare product data
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        image: imageUrls[0] || '', // Keep first image as main image for backward compatibility
        images: imageUrls,
        updatedAt: serverTimestamp()
      };
      
      if (!isEditMode) {
        productData.createdAt = serverTimestamp();
      }
      
      // Save to Firestore
      if (isEditMode) {
        await updateDoc(doc(db, 'products', id), productData);
      } else {
        await addDoc(collection(db, 'products'), productData);
      }
      
      // Navigate back to product list
      navigate('/admin/products');
      
    } catch (error) {
      console.error('Error saving product:', error);
      setError(error.message || 'Error saving product');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/admin/products')}>
          <FaArrowLeft />
        </BackButton>
        <Title>{isEditMode ? 'Edit Product' : 'Add New Product'}</Title>
      </Header>
      
      <FormContainer>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Product Name*</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="category">Category*</Label>
            <Select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              <option value="">Select Category</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="price">Price ($)*</Label>
            <Input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0.01"
              step="0.01"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="stock">Quantity in Stock*</Label>
            <Input
              type="number"
              id="stock"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              min="0"
              step="1"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="weight">Weight</Label>
            <Input
              type="text"
              id="weight"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              placeholder="e.g., 31.1g"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="dimensions">Dimensions</Label>
            <Input
              type="text"
              id="dimensions"
              name="dimensions"
              value={formData.dimensions}
              onChange={handleChange}
              placeholder="e.g., 38.1mm"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="material">Material</Label>
            <Input
              type="text"
              id="material"
              name="material"
              value={formData.material}
              onChange={handleChange}
              placeholder="e.g., Copper, Nickel, etc."
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="condition">Condition</Label>
            <Input
              type="text"
              id="condition"
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              placeholder="e.g., Brilliant Uncirculated"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="origin">Origin</Label>
            <Input
              type="text"
              id="origin"
              name="origin"
              value={formData.origin}
              onChange={handleChange}
              placeholder="e.g., United States"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="year">Year</Label>
            <Input
              type="text"
              id="year"
              name="year"
              value={formData.year}
              onChange={handleChange}
              placeholder="e.g., 2023"
            />
          </FormGroup>
          
          <FormGroup $fullWidth>
            <Label htmlFor="description">Description</Label>
            <RichTextEditorWrapper>
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    [{ 'font': [] }],
                    [{ 'size': ['small', false, 'large', 'huge'] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'script': 'sub'}, { 'script': 'super' }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                    [{ 'align': [] }],
                    ['link', 'blockquote', 'code-block'],
                    ['clean']
                  ]
                }}
                formats={[
                  'header', 'font', 'size',
                  'bold', 'italic', 'underline', 'strike',
                  'script',
                  'color', 'background',
                  'list', 'bullet', 'indent',
                  'align',
                  'link', 'blockquote', 'code-block'
                ]}
                placeholder="Detailed description of the product..."
              />
            </RichTextEditorWrapper>
          </FormGroup>
          
          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
              />
              <Label htmlFor="featured" style={{ margin: 0 }}>
                Featured Product
              </Label>
            </Checkbox>
          </FormGroup>
          
          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                id="inStock"
                name="inStock"
                checked={formData.inStock}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    inStock: isChecked,
                    stock: isChecked ? (prev.stock > 0 ? prev.stock : 1) : 0
                  }));
                }}
              />
              <Label htmlFor="inStock" style={{ margin: 0 }}>
                In Stock
              </Label>
            </Checkbox>
          </FormGroup>
          
          <FormGroup $fullWidth>
            <ImageUploadContainer>
              <Label>Product Images</Label>
              
              {imagePreviews.length > 0 ? (
                <ImagePreviewsContainer>
                  {imagePreviews.map((preview, index) => (
                    <SingleImagePreview key={index}>
                      <img src={preview} alt={`Product preview ${index + 1}`} />
                      <DeleteImageButton 
                        type="button" 
                        onClick={() => removeImage(index)}
                        title="Remove image"
                      >
                        <FaTrash size={14} />
                      </DeleteImageButton>
                    </SingleImagePreview>
                  ))}
                </ImagePreviewsContainer>
              ) : (
                <SingleImagePreview>
                  No images selected
                </SingleImagePreview>
              )}
              
              <UploadButton>
                <FaPlus /> Add Images
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  multiple
                />
              </UploadButton>
              
              {compressionStatus && (
                <CompressionStatus>
                  <FaSpinner className="fa-spin" /> {compressionStatus}
                </CompressionStatus>
              )}
              
              {isUploading && (
                <ProgressBar>
                  <Progress value={uploadProgress} />
                </ProgressBar>
              )}
            </ImageUploadContainer>
          </FormGroup>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <ButtonGroup>
            <Button 
              type="button" 
              onClick={() => navigate('/admin/products')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              $primary 
              disabled={loading || isUploading}
            >
              {loading || isUploading ? (
                <>
                  <FaSpinner className="fa-spin" /> Saving...
                </>
              ) : (
                'Save Product'
              )}
            </Button>
          </ButtonGroup>
        </Form>
      </FormContainer>
    </Container>
  );
};

export default ProductForm;
